/**
 * Monitoring Service
 * Centralized monitoring and alerting for the application
 */

import { ArchitectureValidator, ArchitectureViolation, ValidationResult } from './ArchitectureValidator';

export interface MonitoringConfig {
  enableArchitectureValidation: boolean;
  enablePerformanceMonitoring: boolean;
  enableErrorTracking: boolean;
  validationIntervalMs: number;
  alertThresholds: {
    criticalViolations: number;
    highViolations: number;
    performanceThresholdMs: number;
  };
}

export class MonitoringService {
  private static instance: MonitoringService;
  private architectureValidator: ArchitectureValidator;
  private config: MonitoringConfig;
  private isRunning: boolean = false;

  private constructor() {
    this.architectureValidator = ArchitectureValidator.getInstance();
    this.config = {
      enableArchitectureValidation: true,
      enablePerformanceMonitoring: true,
      enableErrorTracking: true,
      validationIntervalMs: 30000, // 30 seconds
      alertThresholds: {
        criticalViolations: 0,
        highViolations: 3,
        performanceThresholdMs: 3000
      }
    };
  }

  public static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }

  /**
   * Initialize monitoring
   */
  public async initialize(): Promise<void> {
    console.log('🔍 Initializing monitoring service...');
    
    try {
      // Set up error tracking
      if (this.config.enableErrorTracking) {
        this.setupErrorTracking();
      }

      // Set up performance monitoring
      if (this.config.enablePerformanceMonitoring) {
        this.setupPerformanceMonitoring();
      }

      // Set up architecture validation with delay to allow page to load
      if (this.config.enableArchitectureValidation) {
        // Wait 3 seconds for page to load before starting validation
        setTimeout(() => {
          this.architectureValidator.startMonitoring(this.config.validationIntervalMs);
        }, 3000);
      }

      this.isRunning = true;
      console.log('✅ Monitoring service initialized');
    } catch (error) {
      console.error('❌ Failed to initialize monitoring service:', error);
      throw error;
    }
  }

  /**
   * Stop monitoring
   */
  public stop(): void {
    if (this.architectureValidator) {
      this.architectureValidator.stopMonitoring();
    }
    this.isRunning = false;
    console.log('🛑 Monitoring service stopped');
  }

  /**
   * Get current monitoring status
   */
  public getStatus(): {
    isRunning: boolean;
    config: MonitoringConfig;
    violations: ArchitectureViolation[];
  } {
    return {
      isRunning: this.isRunning,
      config: this.config,
      violations: this.architectureValidator.getViolations()
    };
  }

  /**
   * Run a manual validation
   */
  public async runValidation(): Promise<ValidationResult> {
    return await this.architectureValidator.runFullValidation();
  }

  /**
   * Update monitoring configuration
   */
  public updateConfig(newConfig: Partial<MonitoringConfig>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('📝 Monitoring configuration updated:', newConfig);
  }

  /**
   * Set up error tracking
   */
  private setupErrorTracking(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      console.error('🚨 Global error caught:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      console.error('🚨 Unhandled promise rejection:', event.reason);
    });

    // React error boundary integration
    if (typeof window !== 'undefined') {
      (window as any).__REACT_ERROR_BOUNDARY__ = {
        onError: (error: Error, errorInfo: any) => {
          console.error('🚨 React error boundary caught:', error, errorInfo);
        }
      };
    }
  }

  /**
   * Set up performance monitoring
   */
  private setupPerformanceMonitoring(): void {
    if (typeof window === 'undefined' || !window.performance) return;

    // Monitor page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.measurePerformance();
      }, 1000);
    });

    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) { // Tasks longer than 50ms
              console.warn('⚠️  Long task detected:', {
                duration: entry.duration,
                startTime: entry.startTime,
                name: entry.name
              });
            }
          }
        });
        observer.observe({ entryTypes: ['longtask'] });
      } catch (error) {
        console.warn('PerformanceObserver not supported:', error);
      }
    }
  }

  /**
   * Measure current performance metrics
   */
  private measurePerformance(): void {
    if (typeof window === 'undefined' || !window.performance) return;

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigation) {
      const metrics = {
        loadTime: navigation.loadEventEnd - navigation.loadEventStart,
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        firstPaint: 0,
        firstContentfulPaint: 0
      };

      // Get paint metrics if available
      const paintEntries = performance.getEntriesByType('paint');
      paintEntries.forEach((entry) => {
        if (entry.name === 'first-paint') {
          metrics.firstPaint = entry.startTime;
        } else if (entry.name === 'first-contentful-paint') {
          metrics.firstContentfulPaint = entry.startTime;
        }
      });

      console.log('📊 Performance metrics:', metrics);

      // Check against thresholds
      if (metrics.loadTime > this.config.alertThresholds.performanceThresholdMs) {
        console.warn(`⚠️  Load time exceeds threshold: ${metrics.loadTime.toFixed(0)}ms`);
      }
    }
  }

  /**
   * Get performance metrics
   */
  public getPerformanceMetrics(): any {
    if (typeof window === 'undefined' || !window.performance) return null;

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (!navigation) return null;

    return {
      loadTime: navigation.loadEventEnd - navigation.loadEventStart,
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      timeToInteractive: navigation.domInteractive - navigation.navigationStart,
      totalTime: navigation.loadEventEnd - navigation.navigationStart
    };
  }
}
